import type { ApiPaginatedResult, ApiResponse } from '../../features/types/api.type';

/**
 * wrap data in an API response : `{result: T}`
 *
 * ```ts
 * httpClient.get.mockReturnValueOnce(
 *  of(mockApiResponse('PENDING'))
 * );
 * ```
 */
export function mockApiResponse<T>(data: T): ApiResponse<T> {
  return {
    result: data,
    correlationId: 'mock-correlation-id',
    requestId: 'mock-request-id',
  };
}

/** wrap data in an API PAGINATED response : `{result: { items: T[] }}`
 *
 * ```ts
 * httpClient.get.mockReturnValueOnce(
 *  of(mockApiPaginatedResponse([{id:1}, {id:2}]))
 * );
 * ``` */
export function mockApiPaginatedResponse<T>(data: T[]): ApiResponse<ApiPaginatedResult<T>> {
  return {
    result: {
      items: data,
      pageNumber: 1,
      pageSize: 100,
      totalPages: 1,
      totalCount: data.length,
    },
    correlationId: 'mock-correlation-id',
    requestId: 'mock-request-id',
  };
}

/** to easily mock success response type APIs
 *
 * ```ts
 * httpClient.get.mockReturnValueOnce(of(MOCK_SUCCESS_RESPONSE))
 * expect(spyAction).toBe(true);
 * ``` */
export const MOCK_SUCCESS_RESPONSE = mockApiResponse(true);
export const MOCK_FAIL_RESPONSE = mockApiResponse(false);

/** Generic HTTP Error mock
 *
 * ```ts
 * httpClient.get.mockRejectedValueOnce(MOCK_HTTP_ERROR as never);
 * ``` */
export const MOCK_HTTP_ERROR = {
  error: {
    errors: [
      { type: 'Unknown', message: 'an error occurred' },
    ],
  },
} as const;

/** http param for getAll paginated requests
 * @example `expect(httpClient.get).toHaveBeenCalledWith(endpoint, PAGINATION_GET_ALL);`
 */
export const PAGINATION_GET_ALL = { params: { GetAll: true } };
